import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const HeaterTypesPieChart = ({ data }) => {
  const processedData = useMemo(() => {
    // Check for different possible data structures
    const heaterTypes = data?.metadata?.heating_summary?.heater_types || 
                       data?.heating_summary?.heater_types;
    
    if (!heaterTypes) {
      console.log('No heater types data found in:', data);
      return [];
    }
    
    // Filter out zero values and map to the required format
    return Object.entries(heaterTypes)
      .filter(([_, value]) => Number(value) > 0)
      .map(([name, value]) => ({
        name,
        value: Number(value) || 0
      }));
  }, [data]);

  // Log the processed data for debugging
 // console.log('Processed pie chart data:', processedData);

  if (!processedData.length) return null;

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  const totalValue = processedData.reduce((sum, item) => sum + item.value, 0);

  // Custom label rendering function
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Only show label if percentage is significant enough (> 3%)
    if (percent < 0.03) return null;

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="14"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Custom tooltip <Tooltip content={<CustomTooltip />} />
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0].payload;
      return (
        <div className="bg-white p-2 border border-gray-200 rounded shadow">
          <p className="font-semibold">{name}</p>
          <p>{value.toFixed(1)} kWh ({(value / totalValue * 100).toFixed(1)}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="h-[400px]">
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            data={processedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={130}
            fill="#8884d8"
            dataKey="value"
            animationDuration={500}
          >
            {processedData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]} 
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </Pie>
          
          <Legend 
            layout="horizontal" 
            verticalAlign="bottom" 
            align="center"
            formatter={(value, entry) => {
              const { payload } = entry;
              return `${value}: ${payload.value.toFixed(1)} kWh`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default HeaterTypesPieChart;